import { type FormEvent, useEffect, useState } from 'react';
import type { Perfil, PerfilPolitica } from '@pcp/contracts';
import { gerarSenhaProvisoria, passwordIssues } from '@pcp/contracts';
import { ApiRequestError } from '../api/client';
import {
  useCreateUser,
  useMachines,
  useMe,
  useResetUserPassword,
  useResetUserTwoFactor,
  useSaveSecurityPolicy,
  useSecurityPolicy,
  useUsers,
} from '../api/queries';
import { ErrorState, LoadingBlock } from '../components/states';
import { formatDateTime } from '../format';
import { t } from '../i18n/pt-BR';

const POLICY_ROLES: PerfilPolitica[] = ['planner', 'viewer', 'operator', 'importacao', 'financeiro', 'admin'];
const ALL_PROFILES: Perfil[] = ['planner', 'viewer', 'operator', 'importacao', 'financeiro', 'admin'];
/** Senha provisória aleatória que já cumpre a política (gerada no navegador, com gerador criptográfico). */
export const generatePassword = (): string => gerarSenhaProvisoria();

const errorText = (e: unknown) =>
  e instanceof ApiRequestError ? e.message : e ? t.states.datasulDown : null;

/** Mostra a senha provisória uma única vez, com botão de copiar. */
function ShownOnce({ label, password }: { label: string; password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="banner ok" role="status">
      <div>{label}</div>
      <div className="row" style={{ marginTop: 6 }}>
        <code className="secret" data-testid="temp-password">
          {password}
        </code>
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(password)
              .then(() => setCopied(true))
              .catch(() => undefined);
          }}
        >
          {copied ? t.admin.users.copied : t.admin.users.copy}
        </button>
      </div>
      <div className="hint" style={{ marginTop: 6 }}>
        {t.admin.users.shownOnce}
      </div>
    </div>
  );
}

function NewUserForm() {
  const create = useCreateUser();
  const machines = useMachines();
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [perfil, setPerfil] = useState<Perfil>('viewer');
  const [maquina, setMaquina] = useState('');
  const [senha, setSenha] = useState(() => generatePassword());
  const [created, setCreated] = useState<{ nome: string; senha: string } | null>(null);

  const valid =
    /^[^@\s]+@[^@\s]+$/.test(email) &&
    nome.trim().length > 0 &&
    passwordIssues(senha).length === 0 &&
    (perfil !== 'operator' || maquina.length > 0);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    create.mutate(
      { email, nome, perfil, ...(perfil === 'operator' ? { maquina } : {}), senha },
      {
        onSuccess: (u) => {
          setCreated({ nome: u.nome, senha });
          setEmail('');
          setNome('');
          setSenha(generatePassword());
        },
      },
    );
  };

  return (
    <section className="panel" aria-labelledby="new-user-title">
      <h2 id="new-user-title">{t.admin.users.newTitle}</h2>
      <p className="hint">{t.admin.users.newHint}</p>
      <form onSubmit={onSubmit} className="form-grid" noValidate>
        <div className="field">
          <label htmlFor="nu-email">{t.admin.users.email}</label>
          <input id="nu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nu-nome">{t.admin.users.name}</label>
          <input id="nu-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="nu-perfil">{t.admin.users.profile}</label>
          <select
            id="nu-perfil"
            className="select"
            value={perfil}
            onChange={(e) => setPerfil(e.target.value as Perfil)}
          >
            {ALL_PROFILES.map((p) => (
              <option key={p} value={p}>
                {t.roles[p]}
              </option>
            ))}
          </select>
        </div>
        {perfil === 'operator' ? (
          <div className="field">
            <label htmlFor="nu-maquina">{t.admin.users.machine}</label>
            <select
              id="nu-maquina"
              className="select"
              value={maquina}
              onChange={(e) => setMaquina(e.target.value)}
            >
              <option value="" />
              {(machines.data ?? []).map((m) => (
                <option key={m.maquina} value={m.maquina}>
                  {m.descricao}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="nu-senha">{t.admin.users.tempPassword}</label>
          <div className="row">
            <input
              id="nu-senha"
              className="mono"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />
            <button type="button" className="btn ghost" onClick={() => setSenha(generatePassword())}>
              {t.admin.users.generate}
            </button>
          </div>
        </div>
        <div className="field" style={{ alignSelf: 'end' }}>
          <button type="submit" className="btn" disabled={!valid || create.isPending}>
            {t.admin.users.create}
          </button>
        </div>
      </form>
      {errorText(create.error) ? (
        <div className="form-error" role="alert">
          {errorText(create.error)}
        </div>
      ) : null}
      {created ? <ShownOnce label={t.admin.users.created(created.nome)} password={created.senha} /> : null}
    </section>
  );
}

/** Administração (só admin): usuários, senhas provisórias e política de 2FA. */
export function AdminPage() {
  const me = useMe();
  const settings = useSecurityPolicy();
  const save = useSaveSecurityPolicy();
  const users = useUsers();
  const resetTf = useResetUserTwoFactor();
  const resetPw = useResetUserPassword();

  const [selected, setSelected] = useState<PerfilPolitica[]>([]);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [pwFor, setPwFor] = useState<number | null>(null);
  const [pwValue, setPwValue] = useState('');
  const [pwDone, setPwDone] = useState<{ nome: string; senha: string } | null>(null);

  useEffect(() => {
    if (settings.data) setSelected(settings.data['perfis-exigem-2fa']);
  }, [settings.data]);

  const toggle = (role: PerfilPolitica) =>
    setSelected((cur) => (cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role]));
  const dirty =
    !!settings.data && [...selected].sort().join() !== [...settings.data['perfis-exigem-2fa']].sort().join();

  return (
    <>
      <NewUserForm />

      <section className="panel" aria-labelledby="users-title">
        <h2 id="users-title">{t.admin.users.title}</h2>
        {users.isLoading ? <LoadingBlock /> : null}
        {users.error ? <ErrorState error={users.error} onRetry={() => void users.refetch()} /> : null}
        {pwDone ? <ShownOnce label={t.admin.users.passwordSet(pwDone.nome)} password={pwDone.senha} /> : null}
        {users.data ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.admin.users.name}</th>
                  <th>{t.admin.users.login}</th>
                  <th>{t.admin.users.role}</th>
                  <th>{t.admin.users.twoFactor}</th>
                  <th>{t.admin.users.lastLogin}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => {
                  const self = me.data?.login === u.login;
                  return (
                    <tr key={u.id}>
                      <td>
                        {u.nome} {self ? <span className="hint">{t.admin.users.you}</span> : null}
                      </td>
                      <td className="mono">{u.login}</td>
                      <td>{t.roles[u.perfil]}</td>
                      <td>
                        <span className={`chip ${u['dois-fatores-ativo'] ? 'ok' : 'info'}`}>
                          {u['dois-fatores-ativo']
                            ? t.account.twoFactor.statusOn
                            : t.account.twoFactor.statusOff}
                        </span>
                      </td>
                      <td>{u['ultimo-acesso'] ? formatDateTime(u['ultimo-acesso']) : t.admin.users.never}</td>
                      <td>
                        <div className="row">
                          {!self ? (
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => {
                                setPwFor(u.id);
                                setPwValue(generatePassword());
                                setPwDone(null);
                              }}
                            >
                              {t.admin.users.resetPassword}
                            </button>
                          ) : null}
                          {u['dois-fatores-ativo'] ? (
                            confirming === u.id ? (
                              <span
                                className="row"
                                role="group"
                                aria-label={t.admin.users.resetConfirm(u.nome)}
                              >
                                <span className="hint">{t.admin.users.resetConfirm(u.nome)}</span>
                                <button
                                  type="button"
                                  className="btn"
                                  disabled={resetTf.isPending}
                                  onClick={() =>
                                    resetTf.mutate(u.id, { onSettled: () => setConfirming(null) })
                                  }
                                >
                                  {t.admin.users.yes}
                                </button>
                                <button
                                  type="button"
                                  className="btn ghost"
                                  onClick={() => setConfirming(null)}
                                >
                                  {t.admin.users.no}
                                </button>
                              </span>
                            ) : (
                              <button type="button" className="btn ghost" onClick={() => setConfirming(u.id)}>
                                {t.admin.users.reset}
                              </button>
                            )
                          ) : null}
                        </div>
                        {pwFor === u.id ? (
                          <form
                            className="stack"
                            style={{ marginTop: 10, minWidth: 300 }}
                            aria-label={t.admin.users.resetPasswordTitle(u.nome)}
                            onSubmit={(e) => {
                              e.preventDefault();
                              if (passwordIssues(pwValue).length > 0) return;
                              resetPw.mutate(
                                { id: u.id, senha: pwValue },
                                {
                                  onSuccess: () => {
                                    setPwDone({ nome: u.nome, senha: pwValue });
                                    setPwFor(null);
                                  },
                                },
                              );
                            }}
                          >
                            <strong>{t.admin.users.resetPasswordTitle(u.nome)}</strong>
                            <span className="hint">{t.admin.users.resetPasswordHint}</span>
                            <div className="row">
                              <input
                                aria-label={t.admin.users.tempPassword}
                                className="mono"
                                value={pwValue}
                                onChange={(e) => setPwValue(e.target.value)}
                                style={{ flex: 1, minWidth: 160 }}
                              />
                              <button
                                type="button"
                                className="btn ghost"
                                onClick={() => setPwValue(generatePassword())}
                              >
                                {t.admin.users.generate}
                              </button>
                            </div>
                            {errorText(resetPw.error) ? (
                              <div className="form-error" role="alert">
                                {errorText(resetPw.error)}
                              </div>
                            ) : null}
                            <div className="row">
                              <button
                                type="submit"
                                className="btn"
                                disabled={passwordIssues(pwValue).length > 0 || resetPw.isPending}
                              >
                                {t.admin.users.apply}
                              </button>
                              <button type="button" className="btn ghost" onClick={() => setPwFor(null)}>
                                {t.admin.users.cancel}
                              </button>
                            </div>
                          </form>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="panel" aria-labelledby="policy-title">
        <h2 id="policy-title">{t.admin.security.title}</h2>
        <p className="hint">{t.admin.security.description}</p>
        {settings.isLoading ? <LoadingBlock rows={2} /> : null}
        {settings.error ? (
          <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
        ) : null}
        {settings.data ? (
          <>
            <fieldset className="roles">
              <legend className="sr-only">{t.admin.security.title}</legend>
              {POLICY_ROLES.map((role) => (
                <label key={role} className="check">
                  <input type="checkbox" checked={selected.includes(role)} onChange={() => toggle(role)} />
                  <span>{t.roles[role]}</span>
                </label>
              ))}
            </fieldset>
            {errorText(save.error) ? (
              <div className="form-error" role="alert">
                {errorText(save.error)}
              </div>
            ) : null}
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate({ 'perfis-exigem-2fa': selected })}
              >
                {t.admin.security.save}
              </button>
              {save.isSuccess && !dirty ? <span className="chip ok">{t.admin.security.saved}</span> : null}
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
